/**
 * useConversationsQuery — sidebar list. Loads via TanStack Query, hydrates
 * the chat store's conversations slice on success so selectors driven by
 * the store stay the single source of truth.
 *
 * v1 reference: `ando-frontend/app/components/contexts/ConversationContext.tsx`
 *               `loadConversations()` (lines 1379-1411).
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { listConversations, type ConversationSummary } from "@ando/core/api";

import { useChatStore } from "../state/chatStore.js";
import type { ChatConversation } from "../state/types.js";
import { useChatUserId } from "./useChatSession.js";

export function chatConversationsQueryKey(userId: string | null): readonly unknown[] {
  return ["chat", "conversations", userId] as const;
}

/**
 * Sidebar entry as consumed by the UI. The BE wire shape keeps the
 * ``conversation_id`` in the outer key of ``trees``; we re-attach it here
 * so downstream selectors can treat the array as flat objects.
 */
export interface SidebarConversationEntry extends ConversationSummary {
  conversation_id: string;
}

export interface UseConversationsQueryResult {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  summaries: SidebarConversationEntry[];
}

function summaryToConversation(
  userId: string,
  entry: SidebarConversationEntry,
): ChatConversation {
  return {
    id: entry.conversation_id,
    title: entry.title || "",
    user_id: userId,
    queries: [],
    // BE field is ``last_update_time`` (derived from last_message_at ??
    // updated_at ?? created_at) — map it onto our ``updated_at`` slot used by
    // the sidebar sort.
    updated_at: entry.last_update_time,
    config: {
      mode: "fast",
      webSearchMode: false,
      focus: "standard",
      selectedConnectionIds: [],
      agentModeVersionId: null,
      folderFilter: [],
      scopedDocuments: [],
      connectSpace: null,
      terraMode: false,
    },
    replayInProgress: false,
    project_id: entry.project_id ?? null,
  };
}

export function useConversationsQuery(): UseConversationsQueryResult {
  const userId = useChatUserId();
  const hydrate = useChatStore((s) => s.hydrateConversations);
  const setLoading = useChatStore((s) => s.setConversationsLoading);

  const query = useQuery({
    queryKey: chatConversationsQueryKey(userId),
    queryFn: () => {
      if (!userId) throw new Error("useConversationsQuery: no user session");
      return listConversations(userId);
    },
    enabled: userId !== null,
  });

  // Keep the store's loading flag in sync so descendants can render a
  // skeleton without subscribing to TanStack Query directly.
  useEffect(() => {
    setLoading(query.isLoading);
  }, [query.isLoading, setLoading]);

  // Hydrate the store on success. `trees` is the BE-canonical map
  // (conversation_id → summary); ordering follows insertion which is
  // recency-descending in v1 — keep that contract. The BE keeps the id in
  // the outer key, so we attach it onto each value before projection.
  useEffect(() => {
    if (!userId || !query.data) return;
    const list = Object.entries(query.data.trees).map(([conversationId, s]) =>
      summaryToConversation(userId, {
        ...(s as ConversationSummary),
        conversation_id: conversationId,
      }),
    );
    hydrate(list);
  }, [userId, query.data, hydrate]);

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    summaries: query.data
      ? Object.entries(query.data.trees).map(([conversationId, s]) => ({
          ...(s as ConversationSummary),
          conversation_id: conversationId,
        }))
      : [],
  };
}
